import React, { useState } from "react";
import "./Homepage.scss";
import logo from "../images/logo.svg";
import appStoreBadge from "../images/app-store-badge.png";
import googlePlayBadge from "../images/google-play-badge.png";

const HERO_CHIPS = [
  { label: "🛶 Boating", size: "lg", top: 33, left: 98 },
  { label: "⛹️ Basketball", size: "md", top: 187, left: 68, opacity: 0.6 },
  { label: "🏋 Fitness Gym", size: "sm", top: 154, left: 515, opacity: 0.4 },
  { label: "🎳 Bowling", size: "md", top: 299, left: 449 },
  { label: "🎣 Fishing", size: "sm", top: 382, left: 0, opacity: 0.4 },
  { label: "🏔️ Hiking", size: "lg", top: 516, left: 445 },
  { label: "⛳️ Golf", size: "md", top: 577, left: 52 },
  { label: "🧗 Climbing", size: "lg", top: 687, left: 403 },
];

const FEATURE_CARDS = [
  { emoji: "📱", title: "Mobile App", subtitle: "iOS & Android" },
  { emoji: "💻", title: "Web Platform", subtitle: "Full browser access" },
  { emoji: "🗺️", title: "Location-Based Discovery", subtitle: "Real activities near you, updated in real-time" },
  { emoji: "👥", title: "Community", subtitle: "Connect through shared passion" },
];

const WHY_CARDS = [
  {
    emoji: "🔍",
    title: "Find activities faster",
    text: "Discover things to do near you without scrolling through multiple platforms or group chats.",
  },
  {
    emoji: "🤝",
    title: "Meet people naturally",
    text: "Connect with others through shared activities instead of forced networking or random introductions.",
  },
  {
    emoji: "✨",
    title: "Create your own plans",
    text: "Host an activity, invite people to join, and bring your idea to life.",
  },
  {
    emoji: "🏙️",
    title: "Explore your city differently",
    text: "Find new places, events, hobbies, and communities around you.",
  },
  {
    emoji: "📱",
    title: "Built for real life",
    text: "Simple, mobile-first, and focused on helping people actually show up and experience more.",
  },
];

const CATEGORY_CARDS = [
  { emoji: "⛰️", title: "Outdoor adventures", img: "/landing/cat-outdoor.jpg" },
  { emoji: "🥾", title: "Hiking and nature walks", img: "/landing/cat-hiking.jpg" },
  { emoji: "🏋️", title: "Fitness and sports", img: "/landing/cat-fitness.jpg" },
  { emoji: "🏄", title: "Beach and water activities", img: "/landing/cat-water.jpg" },
  { emoji: "🗓️", title: "Weekend plans", img: "/landing/cat-weekend.jpg" },
  { emoji: "🎭", title: "Community experiences", img: "/landing/cat-community.jpg" },
];

const ACTIVITY_CHIPS = [
  "ATV/SXS Vehicle",
  "Archery",
  "Aviation",
  "Backpacking",
  "Baseball",
  "Basketball",
  "Boating",
  "Bowling",
  { label: "Beach", accent: true },
  "Canoeing",
  { label: "Outdoor adventures", accent: true },
  "Canyoneering",
  "Climbing",
  "Climbing Alpine",
  "Climbing Bouldering",
  "Climbing Gym",
  "Climbing Ice",
  "Climbing Sport",
  "Climbing Trad",
  "Cornhole",
  { label: "Cycling", accent: true },
  "Dancing",
  "Disc Golf",
  "Dog Park",
  "Endurance Competition",
  "Fishing",
  { label: "Fitness Gym", accent: true },
  "Fly Fishing",
  "Golf",
  "Gymnastics",
  "Handball",
  { label: "Hiking", accent: true },
  "Hockey",
  "Horseback",
  "Hunting",
  "Hydrofoiling",
  "Jetskiing",
  { label: "Water activities", accent: true },
  "Kayaking Sea",
  "Kayaking Whitewater",
  "Kitesurfing",
  "Motocross",
  "Motorcycling Dirt",
  "Motorcycling Road",
  { label: "Nature walks", accent: true },
];

const AUDIENCE_PARTICIPANT_BULLETS = [
  "Find activities happening near you",
  "Join plans based on your interests",
  "See important activity details before joining",
  "Connect through shared experiences",
  "Keep track of your upcoming activities in one place",
];

const AUDIENCE_HOST_BULLETS = [
  "Create activities in minutes",
  "Add activity details, location, time, and participant rules",
  "Manage requests and invites",
  "Communicate key activity information",
  "Build a local community around your interests",
];

const FEATURE_TILES = [
  {
    icon: "🔍",
    title: "Activity discovery",
    text: "Browse local activities and find plans that match your interests.",
  },
  {
    icon: "📋",
    title: "Activity details",
    text: "View important information before joining, including time, location, participants, and requirements.",
  },
  {
    icon: "📩",
    title: "Join requests and invites",
    text: "Apply to join activities or accept invitations from others.",
  },
  {
    icon: "🛠️",
    title: "Host tools",
    text: "Create, publish, and manage your own activities.",
  },
  {
    icon: "📅",
    title: "Upcoming activities",
    text: "Keep track of activities where you are a host, participant, invited user, or applicant.",
  },
  {
    icon: "👥",
    title: "Profiles and community",
    text: "Understand who is joining or hosting and connect through shared interests.",
  },
];

const FAQ_ITEMS = [
  {
    q: "What is ShareMy?",
    a: "ShareMy is an app for discovering, joining, and creating local activities.",
  },
  {
    q: "Can I create my own activity?",
    a: "Yes. Users can create activities, add details, and invite or approve participants.",
  },
  {
    q: "What kind of activities can I find?",
    a: "You can find outdoor activities, fitness sessions, sports, social meetups, wellness events, creative workshops, and other local experiences.",
  },
  {
    q: "Is ShareMy only for large events?",
    a: "No. ShareMy is built for both small group activities and larger community experiences.",
  },
  {
    q: "How does ShareMy help me meet people?",
    a: "ShareMy connects people through shared interests and real-life activities, making connection feel more natural.",
  },
  {
    q: "Where is ShareMy available?",
    a: "ShareMy is launching locally, starting with Provo, Utah, with more locations planned.",
  },
];

const STEPS = [
  {
    n: "Step 1",
    title: "Browse activities",
    text: "Find activities near you based on your interests, location, and availability.",
    img: "/landing/mockup-step1.png",
  },
  {
    n: "Step 2",
    title: "Join or apply",
    text: "Choose an activity you like, send a request if needed, or join when participation is open.",
    img: "/landing/mockup-step2.png",
  },
  {
    n: "Step 3",
    title: "Create your own activity",
    text: "Have an idea? Host your own activity and invite others to participate.",
    img: "/landing/mockup-step3.png",
  },
  {
    n: "Step 4",
    title: "Show up and enjoy",
    text: "Meet people, explore new places, and experience something together in real life.",
    img: "/landing/mockup-step4.png",
  },
];

const PhoneMockup = ({ src }) => (
  <div className="phone-mockup">
    <img className="phone-mockup__screen" src={src} alt="App screen" />
    <img className="phone-mockup__frame" src="/landing/iphone-frame.png" alt="" aria-hidden />
  </div>
);

const SectionTitle = ({ children }) => <span className="pill">{children}</span>;

const Homepage = () => {
  const [openFaq, setOpenFaq] = useState(1);

  return (
    <div className="home">
      <section className="hero">
        <div className="hero__bg">
          <span className="hero__blob hero__blob--right" />
          <span className="hero__blob hero__blob--left" />
        </div>
        <div className="hero__inner">
          <div className="hero__visual">
            <div className="hero__chips">
              {HERO_CHIPS.map((c, i) => (
                <span
                  key={i}
                  className={`chip chip--${c.size}`}
                  style={{ top: `${c.top}px`, left: `${c.left}px`, opacity: c.opacity ?? 1 }}
                >
                  {c.label}
                </span>
              ))}
            </div>
            <PhoneMockup src="/landing/mockup-hero.png" />
          </div>
          <div className="hero__copy">
            <SectionTitle>Location-Based Activity Platform</SectionTitle>
            <div className="hero__copy-group">
              <h1>
                <span className="accent">Love</span> what you do. <span className="accent">Share</span> the moment. Your{" "}
                <span className="accent">activities</span>. Your <span className="accent">people.</span>{" "}
                <span className="accent">ShareMy...</span>
              </h1>
              <div className="hero__copy-text">
                <p className="lead">
                  ShareMy helps you find activities near you, join experiences that match your interests, and connect with people through real-life moments.
                </p>
                <p className="muted">
                  Whether you want to go hiking, join a fitness session, attend a social meetup, try something new, or create your own activity, ShareMy makes it
                  easy to discover what is happening around you.
                </p>
              </div>
            </div>
            <div className="hero__cta">
              <a
                className="btn btn--primary"
                href="https://apps.apple.com/app/id6737514100"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download the App
              </a>
              <a
                className="btn btn--secondary"
                href="https://production-app-aqk8t.ondigitalocean.app/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Join to Web
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="about" id="features">
        <div className="about__inner">
          <div className="about__copy">
            <div className="about__copy-head">
              <SectionTitle>About ShareMy</SectionTitle>
              <h2>
                What is <span className="accent">ShareMy?</span>
              </h2>
            </div>
            <div className="about__copy-paragraphs">
              <div className="about__copy-paragraph-group">
                <p className="about__lead">ShareMy is an activity marketplace where people can discover, join, and create local activities.</p>
                <p className="muted">
                  Instead of endlessly searching for things to do, users can open the app and quickly find experiences nearby — from outdoor adventures and sports
                  to social events, wellness activities, and community meetups.
                </p>
              </div>
              <p className="italic accent">ShareMy brings people together through shared interests, local experiences, and spontaneous plans.</p>
            </div>
          </div>
          <div className="about__grid">
            {FEATURE_CARDS.map((card) => (
              <div key={card.title} className="feature-card">
                <span className="feature-card__emoji">{card.emoji}</span>
                <h4>{card.title}</h4>
                <p>{card.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="why">
        <div className="why__bg" />
        <div className="why__inner">
          <h2>
            Why Choose <span className="accent">ShareMy?</span>
          </h2>
          <div className="why__grid">
            {WHY_CARDS.map((card) => (
              <div key={card.title} className="why-card">
                <div className="why-card__icon">{card.emoji}</div>
                <h4>{card.title}</h4>
                <p>{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="categories" id="activities">
        <div className="categories__head">
          <div className="categories__head-title">
            <SectionTitle>Activity</SectionTitle>
            <h2>
              Find Activities
              <br />
              That Match Your <span className="accent">Interests</span>
            </h2>
          </div>
          <div className="categories__head-text">
            <p className="muted">
              ShareMy brings different types of local activities into one place, so users can easily explore what fits their mood, schedule, and lifestyle.
            </p>
            <p className="italic light">
              From spontaneous plans to recurring activities, <span className="accent">ShareMy helps you find something worth doing.</span>
            </p>
          </div>
        </div>
        <div className="categories__grid">
          {CATEGORY_CARDS.map((c) => (
            <div key={c.title} className="category-card" style={{ backgroundImage: `url(${c.img})` }}>
              <div className="category-card__overlay" />
              <span className="category-card__title">
                <span className="category-card__icon" aria-hidden>{c.emoji}</span>
                {c.title}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="chips-strip">
        <div className="chips-strip__track">
          {[...ACTIVITY_CHIPS, ...ACTIVITY_CHIPS].map((c, i) => {
            const isObj = typeof c === "object";
            const label = isObj ? c.label : c;
            const accent = isObj && c.accent;
            return (
              <span key={`${label}-${i}`} className={`activity-chip ${accent ? "activity-chip--accent" : ""}`}>
                {label}
              </span>
            );
          })}
        </div>
      </section>

      <section className="how" id="how">
        <div className="how__head">
          <SectionTitle>Process</SectionTitle>
          <h2>How ShareMy Works</h2>
        </div>
        <div className="how__steps">
          {STEPS.map((step, i) => (
            <div key={step.n} className={`step ${i % 2 === 1 ? "step--reverse" : ""}`}>
              <PhoneMockup src={step.img} />
              <div className="step__text">
                <span className="step__n">{step.n}</span>
                <div className="step__body">
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="audience">
        <div className="audience__inner">
          <h2 className="audience__title">
            Built for <span className="accent">Everyone</span>
          </h2>
          <div className="audience__grid">
            <div className="audience-card audience-card--blue">
              <div className="audience-card__inner">
                <span className="audience-card__badge">For Participants</span>
                <div className="audience-card__heading">
                  <h3>
                    Join Activities That
                    <br />
                    Fit Your Lifestyle
                  </h3>
                  <p>
                    ShareMy is for people who want to do more, meet others,
                    <br />
                    and discover new experiences without complicated planning.
                  </p>
                </div>
              </div>
              <ul className="audience-card__list">
                {AUDIENCE_PARTICIPANT_BULLETS.map((b) => (
                  <li key={b}>
                    <span className="audience-card__check" aria-hidden>
                      <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.37035 11.5496C2.37035 11.4196 2.34535 11.3546 2.29535 11.3546L1.95035 11.5196C1.95035 11.4496 1.91035 11.3996 1.83035 11.3696L1.71035 11.3546C1.63035 11.3546 1.53035 11.3896 1.41035 11.4596C1.39035 11.4096 1.36535 11.3596 1.33535 11.3096C1.30535 11.2596 1.28035 11.2146 1.26035 11.1746C1.13035 10.9246 1.00035 10.6496 0.870352 10.3496C0.750352 10.0396 0.635352 9.74465 0.525352 9.46465C0.425352 9.18465 0.345352 8.96465 0.285352 8.80465C0.245352 8.67465 0.200352 8.47965 0.150352 8.21965C0.100352 7.95965 0.0503516 7.62965 0.000351604 7.22965C0.110352 7.29965 0.195352 7.33465 0.255352 7.33465C0.325352 7.33465 0.390352 7.22965 0.450352 7.01965C0.480352 7.05965 0.535352 7.07965 0.615352 7.07965C0.675352 7.07965 0.720352 7.05965 0.750352 7.01965L0.990352 6.65965L1.26035 6.74965H1.27535C1.29535 6.74965 1.31535 6.73965 1.33535 6.71965C1.35535 6.69965 1.38535 6.67965 1.42535 6.65965C1.50535 6.60965 1.56535 6.58465 1.60535 6.58465L1.65035 6.59965C1.90035 6.71965 2.06035 6.93965 2.13035 7.25965C2.31035 8.01965 2.49035 8.39965 2.67035 8.39965C2.85035 8.39965 3.06035 8.20965 3.30035 7.82965C3.42035 7.63965 3.54035 7.41965 3.66035 7.16965C3.79035 6.91965 3.92035 6.63965 4.05035 6.32965C4.07035 6.44965 4.09035 6.50965 4.11035 6.50965C4.16035 6.50965 4.24535 6.38465 4.36535 6.13465C4.49535 5.88465 4.70035 5.53965 4.98035 5.09965C5.14035 4.82965 5.34035 4.52465 5.58035 4.18465C5.83035 3.84465 6.09535 3.49465 6.37535 3.13465C6.65535 2.77465 6.92535 2.43465 7.18535 2.11465C7.45535 1.79465 7.69535 1.51965 7.90535 1.28965C8.11535 1.05965 8.27035 0.909648 8.37035 0.839648C8.75035 0.579648 9.05035 0.329648 9.27035 0.089648C9.26035 0.159648 9.24535 0.224648 9.22535 0.284648C9.21535 0.334648 9.21035 0.369648 9.21035 0.389648C9.21035 0.429648 9.23035 0.449648 9.27035 0.449648L9.69035 0.239648V0.299648C9.69035 0.379648 9.71035 0.419648 9.75035 0.419648C9.78035 0.419648 9.84035 0.374648 9.93035 0.284648C10.0204 0.194648 10.0704 0.129648 10.0804 0.089648L10.0504 0.299648L10.5604 -0.000351727L10.4404 0.269648C10.6004 0.159648 10.7154 0.104648 10.7854 0.104648C10.8254 0.104648 10.8554 0.129648 10.8754 0.179648C10.8954 0.219648 10.9054 0.259648 10.9054 0.299648C10.9054 0.359648 10.8804 0.429648 10.8304 0.509648C10.7804 0.589648 10.7154 0.684648 10.6354 0.794648C10.5754 0.874648 10.4754 0.994648 10.3354 1.15465C10.2054 1.30465 10.0054 1.52965 9.73535 1.82965C9.46535 2.11965 9.10535 2.52465 8.65535 3.04465C8.53535 3.17465 8.35035 3.40465 8.10035 3.73465C7.85035 4.05465 7.56535 4.42965 7.24535 4.85965C6.93535 5.27965 6.62535 5.70465 6.31535 6.13465C6.00535 6.56465 5.73035 6.95465 5.49035 7.30465C5.25035 7.64465 5.08035 7.89965 4.98035 8.06965L4.05035 9.64465C3.85035 9.98465 3.68535 10.2646 3.55535 10.4846C3.42535 10.6946 3.32535 10.8396 3.25535 10.9196C3.10535 11.0996 2.94035 11.2596 2.76035 11.3996L2.62535 11.3246L2.50535 11.3996L2.37035 11.5496Z" fill="currentColor" />
                      </svg>
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="audience-card audience-card--orange">
              <div className="audience-card__inner">
                <span className="audience-card__badge">For Hosts / Activity Leaders</span>
                <div className="audience-card__heading">
                  <h3>
                    Create Activities
                    <br />
                    and Bring People Together
                  </h3>
                  <p>
                    ShareMy also gives users the tools to create and manage their own activities. Whether it is a hike, workout, or local experience, hosts can share the plan,
                    manage requests, and build participation around the activity.
                  </p>
                </div>
              </div>
              <ul className="audience-card__list">
                {AUDIENCE_HOST_BULLETS.map((b) => (
                  <li key={b}>
                    <span className="audience-card__check" aria-hidden>
                      <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.37035 11.5496C2.37035 11.4196 2.34535 11.3546 2.29535 11.3546L1.95035 11.5196C1.95035 11.4496 1.91035 11.3996 1.83035 11.3696L1.71035 11.3546C1.63035 11.3546 1.53035 11.3896 1.41035 11.4596C1.39035 11.4096 1.36535 11.3596 1.33535 11.3096C1.30535 11.2596 1.28035 11.2146 1.26035 11.1746C1.13035 10.9246 1.00035 10.6496 0.870352 10.3496C0.750352 10.0396 0.635352 9.74465 0.525352 9.46465C0.425352 9.18465 0.345352 8.96465 0.285352 8.80465C0.245352 8.67465 0.200352 8.47965 0.150352 8.21965C0.100352 7.95965 0.0503516 7.62965 0.000351604 7.22965C0.110352 7.29965 0.195352 7.33465 0.255352 7.33465C0.325352 7.33465 0.390352 7.22965 0.450352 7.01965C0.480352 7.05965 0.535352 7.07965 0.615352 7.07965C0.675352 7.07965 0.720352 7.05965 0.750352 7.01965L0.990352 6.65965L1.26035 6.74965H1.27535C1.29535 6.74965 1.31535 6.73965 1.33535 6.71965C1.35535 6.69965 1.38535 6.67965 1.42535 6.65965C1.50535 6.60965 1.56535 6.58465 1.60535 6.58465L1.65035 6.59965C1.90035 6.71965 2.06035 6.93965 2.13035 7.25965C2.31035 8.01965 2.49035 8.39965 2.67035 8.39965C2.85035 8.39965 3.06035 8.20965 3.30035 7.82965C3.42035 7.63965 3.54035 7.41965 3.66035 7.16965C3.79035 6.91965 3.92035 6.63965 4.05035 6.32965C4.07035 6.44965 4.09035 6.50965 4.11035 6.50965C4.16035 6.50965 4.24535 6.38465 4.36535 6.13465C4.49535 5.88465 4.70035 5.53965 4.98035 5.09965C5.14035 4.82965 5.34035 4.52465 5.58035 4.18465C5.83035 3.84465 6.09535 3.49465 6.37535 3.13465C6.65535 2.77465 6.92535 2.43465 7.18535 2.11465C7.45535 1.79465 7.69535 1.51965 7.90535 1.28965C8.11535 1.05965 8.27035 0.909648 8.37035 0.839648C8.75035 0.579648 9.05035 0.329648 9.27035 0.089648C9.26035 0.159648 9.24535 0.224648 9.22535 0.284648C9.21535 0.334648 9.21035 0.369648 9.21035 0.389648C9.21035 0.429648 9.23035 0.449648 9.27035 0.449648L9.69035 0.239648V0.299648C9.69035 0.379648 9.71035 0.419648 9.75035 0.419648C9.78035 0.419648 9.84035 0.374648 9.93035 0.284648C10.0204 0.194648 10.0704 0.129648 10.0804 0.089648L10.0504 0.299648L10.5604 -0.000351727L10.4404 0.269648C10.6004 0.159648 10.7154 0.104648 10.7854 0.104648C10.8254 0.104648 10.8554 0.129648 10.8754 0.179648C10.8954 0.219648 10.9054 0.259648 10.9054 0.299648C10.9054 0.359648 10.8804 0.429648 10.8304 0.509648C10.7804 0.589648 10.7154 0.684648 10.6354 0.794648C10.5754 0.874648 10.4754 0.994648 10.3354 1.15465C10.2054 1.30465 10.0054 1.52965 9.73535 1.82965C9.46535 2.11965 9.10535 2.52465 8.65535 3.04465C8.53535 3.17465 8.35035 3.40465 8.10035 3.73465C7.85035 4.05465 7.56535 4.42965 7.24535 4.85965C6.93535 5.27965 6.62535 5.70465 6.31535 6.13465C6.00535 6.56465 5.73035 6.95465 5.49035 7.30465C5.25035 7.64465 5.08035 7.89965 4.98035 8.06965L4.05035 9.64465C3.85035 9.98465 3.68535 10.2646 3.55535 10.4846C3.42535 10.6946 3.32535 10.8396 3.25535 10.9196C3.10535 11.0996 2.94035 11.2596 2.76035 11.3996L2.62535 11.3246L2.50535 11.3996L2.37035 11.5496Z" fill="currentColor" />
                      </svg>
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="features-grid">
        <div className="features-grid__panel">
          <div className="features-grid__content">
            <div className="features-grid__head">
              <SectionTitle>Key App Features</SectionTitle>
              <h2 className="features-grid__title">
                Everything You Need to <span className="accent">Discover and Join Activities</span>
              </h2>
            </div>
            <div className="features-grid__cards">
              <div className="features-grid__row">
                {FEATURE_TILES.slice(0, 3).map((tile) => (
                  <div key={tile.title} className="feature-tile">
                    <span className="feature-tile__icon">{tile.icon}</span>
                    <h4>{tile.title}</h4>
                    <p>{tile.text}</p>
                  </div>
                ))}
              </div>
              <div className="features-grid__row">
                {FEATURE_TILES.slice(3).map((tile) => (
                  <div key={tile.title} className="feature-tile">
                    <span className="feature-tile__icon">{tile.icon}</span>
                    <h4>{tile.title}</h4>
                    <p>{tile.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="matters">
        <div className="matters__inner">
          <div className="matters__head">
            <SectionTitle>Why It Matters</SectionTitle>
            <div className="matters__head-text">
              <h2 className="matters__title">Social Life, Reimagined Offline</h2>
              <p className="matters__desc">
                Most social platforms keep people online. ShareMy is built to help people move from scrolling to doing. It is designed for real-world connection — helping
                users spend more time outside, meet people through shared interests, and make everyday life more active, social, and meaningful.
              </p>
            </div>
          </div>
          <p className="matters__tag">Less scrolling. More doing.</p>
        </div>
      </section>

      <section className="faq" id="faq">
        <div className="faq__inner">
          <div className="faq__head">
            <SectionTitle>FAQ</SectionTitle>
            <h2 className="faq__title">
              Frequently Asked <span className="accent">Questions</span>
            </h2>
          </div>
          <ul className="faq__list">
            {FAQ_ITEMS.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <li key={item.q} className={`faq-item ${isOpen ? "is-open" : ""}`}>
                  <button type="button" className="faq-item__head" onClick={() => setOpenFaq(isOpen ? -1 : i)}>
                    <span className="faq-item__q">{item.q}</span>
                    <span className="faq-item__icon" aria-hidden>
                      {isOpen ? (
                        <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      )}
                    </span>
                  </button>
                  {isOpen && item.a && <div className="faq-item__body">{item.a}</div>}
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section className="cta" id="download">
        <div className="cta__panel">
          <div className="cta__panel-inner">
            <div className="cta__head">
              <SectionTitle>Start Discovering What's Happening Around You</SectionTitle>
              <div className="cta__head-text">
                <h2 className="cta__title">
                  Your next activity, connection,
                  <br />
                  or experience could be closer than you think.
                </h2>
                <p className="cta__subtitle">Download ShareMy and start exploring local activities today.</p>
              </div>
            </div>
            <div className="cta__buttons">
              <a
                className="btn btn--dark"
                href="https://production-app-aqk8t.ondigitalocean.app/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Join to Web
              </a>
              <a
                className="btn btn--primary"
                href="https://apps.apple.com/app/id6737514100"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download the App
              </a>
              <a className="btn btn--secondary" href="#activities">
                Explore Activities
              </a>
            </div>
          </div>
          <div className="cta__glow" />
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer__container">
          <div className="landing-footer__inner">
            <div className="landing-footer__brand">
              <div className="landing-footer__brand-top">
                <img src={logo} alt="ShareMy" className="landing-footer__logo" />
                <p className="landing-footer__tagline">
                  Your activities. Your people. Your journey.
                  <br />
                  Love what you do. Share the moment.
                </p>
              </div>
              <div className="landing-footer__stores">
                <a
                  className="store-badge"
                  href="https://apps.apple.com/app/id6737514100"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                >
                  <img src={appStoreBadge} alt="Download on the App Store" />
                </a>
                <a className="store-badge" href="#download" aria-label="Get it on Google Play">
                  <img src={googlePlayBadge} alt="Get it on Google Play" />
                </a>
                <a
                  className="landing-footer__web"
                  href="https://production-app-aqk8t.ondigitalocean.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Join to Web
                </a>
              </div>
            </div>
            <div className="landing-footer__cols">
              <div className="landing-footer__col">
                <h6>Platform</h6>
                <div className="landing-footer__col-list">
                  <a href="#features">About</a>
                  <a href="#how">How It Works</a>
                  <a href="#features">Features</a>
                  <a href="#activities">Activities</a>
                  <a href="#faq">FAQ</a>
                </div>
              </div>
              <div className="landing-footer__col">
                <h6>Legal</h6>
                <div className="landing-footer__col-list">
                  <a href="/terms-of-use">Terms of Use</a>
                  <a href="/privacy-policy">Privacy Policy</a>
                  <a href="/cookie-policy">Cookie Policy</a>
                  <a href="/community-guide">Community Guidelines</a>
                  <a href="/safety-guide">Safety Guidelines</a>
                </div>
              </div>
              <div className="landing-footer__col">
                <h6>Contact</h6>
                <div className="landing-footer__col-list">
                  <a href="mailto:support@sharemy.com">support@sharemy.com</a>
                </div>
              </div>
            </div>
          </div>
          <div className="landing-footer__bottom">
            <p>© {new Date().getFullYear()} ShareMy. All rights reserved.</p>
            <p className="landing-footer__bottom-italic">Find your people through shared experiences.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
